import { Button, InputLabel, Stack } from "@mui/material";
import { DialogWindow } from "./DialogWindow";
import { useState } from "react";
import { Confirmation } from "./Confirmation";
import { Spinner } from "UI/Spinner";
import { useForm } from "react-hook-form";
import { FormTextField } from "UI/FormTextField";
import { emailValidation, phoneNumberValidation } from "models/constants";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { Captcha } from "./Captcha";

const defaultValues = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  code: "",
  captchaId: "",
  organizationId: "",
};

export const AddNewSenderDialog = ({
  handleClose,
  setError,
  addNewSender,
  currentButton,
  originator,
  setSelectedSenders,
}) => {
  const [confirmation, setConfirmation] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues,
  });

  const confirmedClose = () => {
    if (isDirty) {
      setConfirmation({
        title: "You have unsaved changes",
        message: "Are you sure you want to close this dialog?",
        proceedFn: () => handleClose(),
        cancelFn: () => setConfirmation(null),
      });
      return;
    }
    handleClose();
  };

  const trimAllValuesInObject = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = obj[key]?.trim();
      return acc;
    }, {});
  };

  const onSubmit = async (newValues) => {
    if (!isDirty) return;

    try {
      const buttonId = Object.keys(currentButton)[0];
      if (!buttonId) return;
      setIsUpdating(true);

      const sender = trimAllValuesInObject(newValues);

      sender.organizationId = originator?.organizationId;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/button_senders`,
        {
          method: "PUT",
          body: JSON.stringify({
            ...sender,
            accessToken: originator?.dashboardAccessToken ?? "",
          }),
        }
      );

      const result = await response.json();
      if (result?.error || result?.message) {
        setError(result?.error || result?.message);
        setIsUpdating(false);
        return;
      }
      if(result?.sender?.id) {
        addNewSender(result?.sender);
        const setter = (prev) => {
          const newValue = [...prev, result?.sender?.id];
          return newValue; 
        };
        setSelectedSenders(setter);
        handleClose();
        return;
      }
      setError("Something went wrong");
    } catch (e) {
      setError(e?.message ?? "Something went wrong");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <DialogWindow
      title={<div className="font-bold">Add New Sender</div>}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex text-left flex-col mt-4 gap-1 select-none">
            <div className="text-sm sm:text-lg">
              Please fill out the form below to add a new sender to:
            </div>

            <Stack direction={"row"} justifyContent={"space-between"} gap={1}>
              <Stack direction={"column"} alignItems={"flex-start"}>
                <InputLabel htmlFor="firstName">First Name</InputLabel>
                <FormTextField
                  id="firstName"
                  name="firstName"
                  control={control}
                  rules={{ required: "is required" }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack direction={"column"} alignItems={"flex-start"}>
                <InputLabel htmlFor="lastName">Last Name</InputLabel>
                <FormTextField
                  id="lastName"
                  name="lastName"
                  control={control}
                  rules={{ required: "is required" }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
            <Stack direction={"column"} alignItems={"flex-start"}>
              <InputLabel htmlFor="email">Email</InputLabel>
              <FormTextField
                id="email"
                name="email"
                control={control}
                rules={emailValidation}
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="phone">Phone</InputLabel>
              <FormMaskedTextField
                id="phone"
                name="phone"
                control={control}
                rules={phoneNumberValidation}
                mask="999-999-9999"
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Stack>
            <Stack alignItems={"flex-start"}>
              <InputLabel htmlFor="jobTitle">Job title (optional)</InputLabel>
              <FormTextField
                id="jobTitle"
                name="jobTitle"
                control={control}
                InputProps={{
                  autoComplete: "off",
                }}
              />
            </Stack>
            <Captcha
              control={control}
              setValue={setValue}
              setError={setError}
            />
            <Stack
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              gap={1}
              p={2}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={confirmedClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!isDirty || isUpdating}
                type="submit"
              >
                Add
              </Button>
            </Stack>
          </div>
          {confirmation && <Confirmation {...confirmation} />}
          {isUpdating && <Spinner />}
        </form>
      }
      handleClose={confirmedClose}
      withCloseButton
    />
  );
};
